/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, isEqual } from 'lodash-es';
import classnames from 'classnames';
import { injectScript } from 'client/utils/inject-script';
import { InventoryEntities } from 'client/data/models/inventory';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { CARCODE_CONFIG_ENTITY, CARCODE_SDK_INITIALIZED_MESSAGE } from 'site-modules/shared/constants/carcode';
import { TrackingConstant } from 'client/tracking/constant';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import { logger } from 'client/utils/logger/logger';
import { Button, Col, Row } from 'reactstrap';
import { isAutonation } from 'site-modules/shared/utils/dealer-details-utils';
import { DealerInfoEntities, DealerInfoModel, getDealerInfoPath } from 'client/data/models/dealer-info';
import { ActivEngageChat } from 'site-modules/shared/components/activ-engage-chat/activ-engage-chat';
import {
  CARCODE_CONFIG,
  TABS,
  TABS_MOBILE,
  CALL_TAB_ID,
  EMAIL_TAB_ID,
  CHAT_TAB_ID,
  TEXT_TAB_ID,
  MESSENGER_TAB_ID,
} from 'site-modules/shared/constants/carcode-price-summary';
import { EventToolbox } from 'client/utils/event-toolbox';
import { CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { withCarcodeConfigCheck } from 'site-modules/shared/components/inventory/with-carcode-config-check/with-carcode-config-check';

import './carcode-price-summary.scss';

/**
 * Get carcode tracking data
 * @param {string} franchiseId
 * @param {string} pageName
 * @param {string} pageCategory
 * @param {Node} wrapper
 * @return {Object}
 */
function getCarCodeTrackingParams(franchiseId, pageName, pageCategory, wrapper) {
  return {
    gtm: {
      synpartner: TrackingConstant.SYNPARTNER,
      page_name: pageName,
      page_category: pageCategory,
      creative_id: getCreativeId(wrapper),
      event_data: {
        partner_id: franchiseId,
      },
    },
  };
}

export const getActivEngageChatConfig = (dealershipInfo, launchSource) => {
  const dealerWebSite = get(dealershipInfo, 'dealerWebSites[0]', '');
  const parentDealershipName = get(dealershipInfo, 'parentDealershipName', '');
  return isAutonation(parentDealershipName)
    ? {
        launchSource,
        dealerWebSite,
      }
    : null;
};

function getDisabledWidgets(carcodeConfig) {
  const result = new Set();

  if (!carcodeConfig.isEdmundsChatActive) {
    result.add(CHAT_TAB_ID);
  }

  if (!carcodeConfig.isTextActive) {
    result.add(TEXT_TAB_ID);
  }

  if (!carcodeConfig.isFacebookActive) {
    result.add(MESSENGER_TAB_ID);
  }

  return result;
}

class CarcodePriceSummaryUI extends Component {
  static propTypes = {
    customConfig: CARCODE_CONFIG_ENTITY,
    dealerInfo: InventoryEntities.DealerInfo,
    dealershipInfo: DealerInfoEntities.DealerInfo,
    carCodeWidgetsUrl: PropTypes.string.isRequired,
    page: PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
    }),
    legacy: PropTypes.shape({
      pageName: PropTypes.string,
      categoryName: PropTypes.string,
    }),
    activEngageConfigLaunchSource: PropTypes.string,
    emailTabContent: PropTypes.node,
    isMobile: PropTypes.bool,
    phoneNumber: PropTypes.string,
    carcodeConfig: PropTypes.shape({
      isFacebookActive: PropTypes.bool,
      isTextActive: PropTypes.bool,
      isEdmundsChatActive: PropTypes.bool,
    }),
    useChatOnline: PropTypes.bool,
  };

  static defaultProps = {
    customConfig: {},
    dealerInfo: null,
    dealershipInfo: null,
    page: null,
    legacy: null,
    activEngageConfigLaunchSource: '',
    emailTabContent: null,
    isMobile: false,
    phoneNumber: '',
    carcodeConfig: {},
    useChatOnline: false,
  };

  state = {
    selected: EMAIL_TAB_ID,
    injectScriptFailed: false,
    carcodeWidgetInitialized: false,
  };

  componentDidMount() {
    const { dealerInfo, dealershipInfo, carCodeWidgetsUrl, page, legacy, customConfig, activEngageConfigLaunchSource } =
      this.props;
    const rooftopId = get(dealerInfo, 'rooftopId', '');
    const franchiseId = get(dealerInfo, 'franchiseId', '');
    const src = `${carCodeWidgetsUrl}/edmunds/${rooftopId}.js`;
    const legacyPageName = get(legacy, 'pageName', '');
    const legacyCategoryName = get(legacy, 'categoryName', '');
    const pageName = get(page, 'name', '');
    const pageCategory = get(page, 'category', '');

    window.__carcode = {
      tracking: getCarCodeTrackingParams(
        franchiseId,
        legacyPageName || pageName,
        legacyCategoryName || pageCategory,
        this.wrapper
      ),
      ...CARCODE_CONFIG,
      ...customConfig,
    };

    this.initActivEngageChat(getActivEngageChatConfig(dealershipInfo, activEngageConfigLaunchSource));

    window.addEventListener('message', this.carcodeMessagesListener);

    injectScript(src).catch(() => {
      this.setState({ injectScriptFailed: true });
      logger.warn('Inject CarCode script error');
    });

    this.fireTracking();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.dealershipInfo, this.props.dealershipInfo)) {
      this.destroyActivEngageChat();

      const { dealershipInfo, activEngageConfigLaunchSource } = this.props;

      this.initActivEngageChat(getActivEngageChatConfig(dealershipInfo, activEngageConfigLaunchSource));
    }
  }

  componentWillUnmount() {
    const event = new CustomEvent('carcode', { detail: 'destroy' });
    document.dispatchEvent(event);
    window.removeEventListener('message', this.carcodeMessagesListener);
  }

  setWrapper = ref => {
    this.wrapper = ref;
  };

  widget = null;
  chatCloseBtn = null;
  smsCloseBtn = null;
  activeEngageChatController = null;

  openEmailForm = () => {
    this.setState({ selected: EMAIL_TAB_ID });
  };

  openChat = () => {
    // listeners of reference-free element will be removed by garbage collector
    this.smsCloseBtn = null;
    const { dealershipInfo, activEngageConfigLaunchSource } = this.props;
    const config = getActivEngageChatConfig(dealershipInfo, activEngageConfigLaunchSource);

    this.setState({ selected: !config ? CHAT_TAB_ID : null }, () => {
      if (config) {
        this.activeEngageChatController.startChat();
      } else {
        this.widget?.openChat();
      }

      if (!this.chatCloseBtn) {
        this.chatCloseBtn = document.querySelector('[data-test="carcode-chat-collapse"]');

        if (this.chatCloseBtn) {
          this.chatCloseBtn.addEventListener('click', this.openEmailForm, false);
        }
      }
    });
  };

  openText = () => {
    // listeners of reference-free element will be removed by garbage collector
    this.chatCloseBtn = null;

    this.setState({ selected: TEXT_TAB_ID }, () => {
      this.widget?.openSms();
      this.widget?.attachSmsWidget();

      if (!this.smsCloseBtn) {
        this.smsCloseBtn = document.querySelector('[data-test="carcode-sms-close"]');

        if (this.smsCloseBtn) {
          this.smsCloseBtn.addEventListener('click', this.openEmailForm, false);
        }
      }
    });
  };

  openFacebook = () => {
    this.widget?.openFacebook();
  };

  initActivEngageChat = config => {
    if (config) {
      this.activeEngageChatController = new ActivEngageChat({ ...config });
      this.activeEngageChatController.init();
      window.__carcode.chatClickHandler = () => this.activeEngageChatController.startChat();
    }
  };

  destroyActivEngageChat = () => {
    this.activeEngageChatController = null;
    window.__carcode.chatClickHandler = null;
  };

  fireTracking = () => {
    const { dealerInfo, carcodeConfig } = this.props;
    const franchiseId = get(dealerInfo, 'franchiseId', '');
    const disabledWidgets = getDisabledWidgets(carcodeConfig);

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: TrackingConstant.SHOW_CARCODE_EMBEDDED,
        creative_id: `${CREATIVE_ID.USED_SINGLE_VDP_EMBEDDED_PRICING}_lead_form`,
        partner_id: franchiseId,
        value: [CHAT_TAB_ID, TEXT_TAB_ID, MESSENGER_TAB_ID].filter(tabId => !disabledWidgets.has(tabId)).join(),
      },
    });
  };

  firePhoneTracking = () => {
    const { phoneNumber } = this.props;
    const franchiseId = get(this.props.dealerInfo, 'franchiseId', '');
    const locId = get(this.props.dealerInfo, 'dealerId', '');

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_DEALER_ENGAGEMENT,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        subaction_name: TrackingConstant.CALL_DEALER,
        creative_id: 'panel_sms_chat_form',
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        f_id: franchiseId,
        loc_id: locId,
        value: phoneNumber,
      },
    });
  };

  carcodeMessagesListener = event => {
    if (event.data?.type === CARCODE_SDK_INITIALIZED_MESSAGE) {
      this.widget = new window.CarcodeWidget();
      this.setState({ carcodeWidgetInitialized: true });
      window.removeEventListener('message', this.carcodeMessagesListener);
    }
  };

  render() {
    const { emailTabContent, isMobile, phoneNumber, carcodeConfig, useChatOnline } = this.props;
    const { selected, injectScriptFailed, carcodeWidgetInitialized } = this.state;
    const wrapperClasses = `carcode-price-summary ${
      isMobile
        ? ''
        : 'px-0_5 px-md-1 mb-1 mb-md-0 mx-0_25 mx-md-0_5 bg-gray-extra-light rounded-bottom border-top-0 py-1'
    }`;

    if (useChatOnline) {
      return (
        <div className="d-inline-block px-0 pos-r pt-0_5">
          <span className="icon-bubbles" aria-hidden />
          <span className="d-inline-flex align-items-center gap-0_25 ms-0_5">
            <Button
              style={{ lineHeigh: '1rem', fontSize: '1rem' }}
              className="btn-link p-0 text-transform-none text-decoration-underline fw-normal"
              onClick={this.openChat}
              color="link"
            >
              Chat Online
            </Button>{' '}
            with Dealer
          </span>
        </div>
      );
    }

    if (injectScriptFailed) return emailTabContent && <div className={wrapperClasses}>{emailTabContent}</div>;

    const disabledWidgets = getDisabledWidgets(carcodeConfig);

    return (
      <div className={wrapperClasses}>
        <Row className={classnames('tabs m-0 text-center', { rounded: !isMobile })}>
          {isMobile
            ? TABS_MOBILE.filter(({ tabId }) => !disabledWidgets.has(tabId)).map(
                ({ tabId, onClickHandler, icon, title }) => (
                  <Col className="p-0" key={tabId}>
                    <Button
                      className="btn-link car-code-chat-btn text-transform-none text-decoration-none text-primary-darker p-0 bg-transparent"
                      onClick={this[onClickHandler]}
                      href={tabId === CALL_TAB_ID ? `tel:${phoneNumber}` : null}
                      data-testid={`carcode-${tabId}-button`}
                      disabled={!carcodeWidgetInitialized && tabId !== CALL_TAB_ID}
                    >
                      <div className="circled-icon rounded-circle m-auto d-flex justify-content-center align-items-center">
                        <div className={classnames('display-lg', icon)} />
                      </div>
                      <div className="fw-normal text-info mt-0_25">{title}</div>
                    </Button>
                  </Col>
                )
              )
            : TABS.filter(({ tabId }) => !disabledWidgets.has(tabId)).map(
                ({ tabId, classes = '', onClickHandler, icon, title }) => (
                  <Col className="p-0" key={tabId}>
                    <Button
                      className={classnames(
                        'btn-link p-0_5 car-code-chat-btn text-transform-none w-100 d-flex flex-column flex-xl-row justify-content-center align-items-center',
                        classes,
                        {
                          'text-info': selected !== tabId,
                          'text-white bg-primary': selected === tabId,
                        }
                      )}
                      onClick={this[onClickHandler]}
                      data-testid={`carcode-${tabId}-button`}
                    >
                      <div className={icon} aria-hidden />
                      <div className="small fw-bold ms-xl-0_25 mt-0_25 mt-xl-0">{title}</div>
                    </Button>
                  </Col>
                )
              )}
        </Row>

        {emailTabContent && (
          <div className={classnames('pt-1', { 'd-none': selected !== EMAIL_TAB_ID })}>{emailTabContent}</div>
        )}
        <div
          ref={this.setWrapper}
          className={classnames('widget-container', selected, { 'd-none': selected === EMAIL_TAB_ID })}
        />
      </div>
    );
  }
}

/**
 * Taking from redux state directly
 * @param state
 */
const mapStateToProps = state => ({
  carCodeWidgetsUrl: state.config.carCodeWidgetsUrl,
  isMobile: state.mobile,
});

const stateToPropsConfig = {
  page: bindToPath('page', PageModel),
  legacy: bindToPath('legacy', PageModel),
  dealershipInfo: bindToPath(({ dealerInfo }) => getDealerInfoPath({ dealerInfo }), DealerInfoModel),
  carcodeConfig: bindToPath(({ dealerInfo }) => {
    const rooftopId = dealerInfo && dealerInfo.rooftopId;
    return rooftopId ? `carcodeConfig.${rooftopId}` : '';
  }, DealerInfoModel),
};

export const CarcodePriceSummary = connect(mapStateToProps)(
  connectToModel(withCarcodeConfigCheck(CarcodePriceSummaryUI), stateToPropsConfig)
);
